import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { hasPermission, hasPermissionsOfAtLeastOne } from "@/services/permissionService"
import { PERMISSIONS } from "@/services/permission"
import { hasMoreRows } from "@/services/listAnswer"

let lastPage = -1

/** Any of these lets a customer be read at all. */
const CUSTOMER_VIEW_PERMISSIONS = [
  PERMISSIONS.VIEW_CUSTOMER_GENERAL,
  PERMISSIONS.VIEW_CUSTOMER_CONTACT,
  PERMISSIONS.VIEW_CUSTOMER_MACHINE,
  PERMISSIONS.VIEW_CUSTOMER_MATERIAL,
  PERMISSIONS.VIEW_CUSTOMER_ADDITIONAL_EFFORT,
  PERMISSIONS.VIEW_CUSTOMER_FILE,
  PERMISSIONS.VIEW_CUSTOMER_USERPOSITION,
  PERMISSIONS.VIEW_CUSTOMER_SETTINGS,
]

export const CUSTOMER_FORM_NAMES = {
  GENERAL: "general",
  CONTACTS: "contacts",
  MATERIALS: "materials",
  MACHINES: "machines",
  ADDITIONAL_EFFORTS: "additionalEfforts",
  FILES: "files",
  USER_POSITIONS: "userPositions",
  CONFIGURATION: "configuration",
  ACTIVITY: "activity",
}

const _blankCustomer = () => {
  return {
    id: "",
    company: "",
    address: {},
    materials: null,
    machines: null,
    additionalEfforts: null,
    contacts: null,
    note: null,
    files: null,
    status: null,
    userPositions: null,
    configuration: {
      defaultCurrency: null,
    },
    createdAt: null,
    createdBy: null,
    lastModifiedAt: null,
    lastModifiedBy: null,
  }
}

export const useCustomerStore = defineStore("customer", {
  state: () => ({
    customers: [],
    customer: _blankCustomer(),
  }),
  getters: {
    getCustomers: state => state.customers,
    getCustomer: state => state.customer,
  },
  actions: {
    setCustomers(customers) {
      this.customers = [...customers]
    },
    addCustomers(customers) {
      customers.forEach(customer => this.customers.push(customer))
    },
    clearCustomers() {
      this.customers = []
    },
    setCustomer(customer) {
      this.customer = customer
    },
    clearCustomer() {
      this.customer = _blankCustomer()
    },
    addContact({ customerId, contact }) {
      return apiClient
        .put(`/customer/${customerId}/contact`, contact, { headers: { tenant: true, authorization: true } })
        .then(res => {
          return res
        })
    },
    addMaterial({ customerId, material }) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_MATERIAL).value) return null

      return apiClient
        .put(`/customer/material/${customerId}`, material, { headers: { tenant: true, authorization: true } })
        .then(res => {
          return res
        })
    },
    addMaterials({ customerId, materials }) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_MATERIAL).value) return null

      return apiClient
        .put(`/customer/material/multiple/${customerId}`, materials, { headers: { tenant: true, authorization: true } })
        .then(res => {
          return res
        })
    },
    addAdditionalEffort({ customerId, additionalEffort }) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_ADDITIONAL_EFFORT).value) return null

      return apiClient
        .put(`/customer/additionaleffort/${customerId}`, additionalEffort, {
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          return res
        })
    },
    addAdditionalEfforts({ customerId, additionalEfforts }) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_ADDITIONAL_EFFORT).value) return null

      return apiClient
        .put(`/customer/additionaleffort/multiple/${customerId}`, additionalEfforts, {
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          return res
        })
    },
    addMachine({ customerId, machine }) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_MACHINE).value) return null

      return apiClient
        .put(`/customer/machine/${customerId}`, machine, { headers: { tenant: true, authorization: true } })
        .then(res => {
          return res
        })
    },
    addMachines({ customerId, machines }) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_MACHINE).value) return null

      return apiClient
        .put(`/customer/machine/multiple/${customerId}`, machines, { headers: { tenant: true, authorization: true } })
        .then(res => {
          return res
        })
    },
    updateCustomer(customer) {
      if (
        !hasPermissionsOfAtLeastOne([
          PERMISSIONS.EDIT_CUSTOMER_GENERAL,
          PERMISSIONS.EDIT_CUSTOMER_CONTACT,
          PERMISSIONS.EDIT_CUSTOMER_MACHINE,
          PERMISSIONS.EDIT_CUSTOMER_MATERIAL,
          PERMISSIONS.EDIT_CUSTOMER_ADDITIONAL_EFFORT,
          PERMISSIONS.EDIT_CUSTOMER_FILE,
          PERMISSIONS.EDIT_CUSTOMER_USERPOSITION,
          PERMISSIONS.EDIT_CUSTOMER_SETTINGS,
        ])
      )
        return null

      return apiClient
        .put(`/customer/${customer.id}`, customer, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setCustomer(res.data)
          }
          return res
        })
    },
    createCustomer(customer, files = []) {
      if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER_GENERAL)) return null

      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append('customer', new Blob([JSON.stringify(customer)], { type: 'application/json' }))
        files.forEach(file => formData.append('files', file))
        return apiClient.post('/customer/with-files', formData, { headers: { tenant: true, authorization: true } })
          .then(res => {
            if (res.status === 201) this.setCustomer(res.data)
            return res
          })
      }
      return apiClient.post("/customer", customer, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 201) {
          this.setCustomer(res.data)
        }
        return res
      })
    },
    loadCustomer(id) {
      if (
        !hasPermissionsOfAtLeastOne(CUSTOMER_VIEW_PERMISSIONS)
      )
        return null

      return apiClient.get(`/customer/${id}`, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          this.setCustomer(res.data)
        }
        return res
      })
    },
    loadCustomers({ page, search, sortBy, sortDesc, selected, itemsPerPage, referenceType, referenceId, archive }) {
      if (
        !hasPermissionsOfAtLeastOne(CUSTOMER_VIEW_PERMISSIONS)
      )
        return null

      return apiClient
        .get("/customer", {
          params: {
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
            // The "only customers that point at this record" narrowing an overview arrives with.
            // Undefined when there is none, and axios drops undefined params.
            referenceType: referenceType,
            referenceId: referenceId,
            // `ARCHIVED` for the overview's archive; undefined lists the customers in use (docs/archiving.md).
            archive: archive,
          },
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            if (page === 0) {
              this.setCustomers(res.data.content)
            } else if (lastPage === page) {
              this.setCustomers(res.data.content)
            } else if (lastPage === page - 1) {
              this.addCustomers(res.data.content)
            } else {
              this.setCustomers(res.data.content)
            }
            lastPage = page
          }
          return res
        })
    },
    /**
     * A page of customers for the project's customer field (`LewAutocompleteCustomer`), returned rather
     * than written into the store: the store's list is the customer overview's rows. Silent, as every
     * picker's search is.
     */
    searchCustomers({ search, page = 0, itemsPerPage }) {
      if (!hasPermissionsOfAtLeastOne(CUSTOMER_VIEW_PERMISSIONS).value) return Promise.resolve({ items: [], hasMore: false })

      return apiClient
        .get("/customer", {
          params: { page, itemsPerPage, search: search || undefined },
          headers: { tenant: true, authorization: true },
          silent: true,
        })
        .then(res => ({ items: res.data?.content ?? [], hasMore: hasMoreRows(res.data, itemsPerPage) }))
    },
  },
})
